import z from 'zod';
import { defineMutatorWithType, type ServerTransaction } from '@rocicorp/zero';
import {
  BlockDraftAsyncDomain,
  PublishedWorkoutStatusDomain,
} from '@strength/domain';
import type { OrgSessionData } from '@strength/server-auth';
import { type Schema, zql } from '../zero-schema.gen';
/**
 * Publish/print and workout-status command mutators (SC-5607). Each has two
 * branches: on the server, delegate to the injected authoritative write (the
 * shared services under `libs/trpc-server/src/lib/block/published/`); on the
 * client, apply the optimistic write.
 *
 * This file and its access map are shared with the Plan slice's block command
 * mutators (SC-5596) — extend, don't fork.
 */

/** Mutator args: the shared tRPC command inputs, verbatim (org + team scope are enforced server-side against the block's own team, fetched by the service, so no `teamId` rides the payload). */
export type PublishBlockArgs = z.infer<
  typeof BlockDraftAsyncDomain.Publish.shape.input
>;
export type PrintBlockArgs = z.infer<
  typeof BlockDraftAsyncDomain.Print.shape.input
>;
export type DeletePrintTaskArgs = z.infer<
  typeof BlockDraftAsyncDomain.DeletePrintTask.shape.input
>;
export type CancelBlockWorkoutArgs = z.infer<
  typeof PublishedWorkoutStatusDomain.CancelBlockWorkout.shape.input
>;
export type UpdateAthleteWorkoutStatusArgs = z.infer<
  typeof PublishedWorkoutStatusDomain.UpdateAthleteWorkoutStatus.shape.input
>;

type ServerTx = ServerTransaction<Schema, unknown>;

/**
 * Block mutator context: the enriched {@link OrgSessionData} plus
 * `serverMutations`, the server-injected authoritative writes (injected, not
 * imported, so the server-only service tree never reaches the web bundle;
 * undefined on the client). Same shape as {@link TrackMutatorCtx}.
 */
export type BlockMutatorCtx = OrgSessionData & {
  serverMutations?: {
    runPublish: (tx: ServerTx, args: PublishBlockArgs) => Promise<void>;
    runPrint: (tx: ServerTx, args: PrintBlockArgs) => Promise<void>;
    runDeletePrintTask: (
      tx: ServerTx,
      args: DeletePrintTaskArgs,
    ) => Promise<void>;
  };
};

/** Workout-status mutator context — same injected-bridge shape as {@link BlockMutatorCtx}, for the `workoutStatus` namespace. */
export type WorkoutStatusMutatorCtx = OrgSessionData & {
  serverMutations?: {
    runCancelBlockWorkout: (
      tx: ServerTx,
      args: CancelBlockWorkoutArgs,
    ) => Promise<void>;
    runUpdateAthleteWorkoutStatus: (
      tx: ServerTx,
      args: UpdateAthleteWorkoutStatusArgs,
    ) => Promise<void>;
  };
};

const defineBlockMutator = defineMutatorWithType<
  Schema,
  BlockMutatorCtx,
  unknown
>();
const defineWorkoutStatusMutator = defineMutatorWithType<
  Schema,
  WorkoutStatusMutatorCtx,
  unknown
>();

async function getServerBranch(
  ctx: { serverMutations?: { [k: string]: unknown } },
  run: string,
): Promise<(tx: ServerTx, args: never) => Promise<void>> {
  const fn = ctx.serverMutations?.[run];
  if (!fn) {
    throw new Error(`block.${run}: server impl not injected`);
  }
  return fn as (tx: ServerTx, args: never) => Promise<void>;
}

/** `block` namespace mutators. */
export const blockMutators = {
  publish: defineBlockMutator(
    BlockDraftAsyncDomain.Publish.shape.input,
    async ({ tx, ctx, args }) => {
      if (tx.location === 'server') {
        const run = await getServerBranch(ctx, 'runPublish');
        await run(tx, args as never);
        return;
      }

      // Deliberately NO optimistic insert: the server generates the task id,
      // so a client id would briefly duplicate the row (publish chip flicker).
      // The authoritative task row syncs back after the server branch runs.
    },
  ),

  print: defineBlockMutator(
    BlockDraftAsyncDomain.Print.shape.input,
    async ({ tx, ctx, args }) => {
      if (tx.location === 'server') {
        const run = await getServerBranch(ctx, 'runPrint');
        await run(tx, args as never);
        return;
      }

      // Optimistic pending insert. The client-generated printTaskId IS the
      // server row's primary key, so the authoritative insert reconciles by
      // id instead of duplicating. Every server-default column is set
      // explicitly: createdById and the full parsed printOptions
      // (SC-3656 parity), plus a fresh createdAt stamp.
      await tx.mutate.BlockPrintTask.insert({
        id: args.printTaskId,
        orgId: ctx.orgId,
        blockId: args.blockId,
        workoutIds: args.workoutIds,
        profileIds: args.profileIds,
        printOptions: args.printOptions,
        status: 'pending',
        pdfUrl: null,
        createdById: ctx.profileId,
        createdAt: new Date().toISOString(),
        updatedAt: null,
        updatedById: null,
      });
    },
  ),

  deletePrintTask: defineBlockMutator(
    BlockDraftAsyncDomain.DeletePrintTask.shape.input,
    async ({ tx, ctx, args }) => {
      if (tx.location === 'server') {
        const run = await getServerBranch(ctx, 'runDeletePrintTask');
        await run(tx, args as never);
        return;
      }

      // Optimistic delete, guarded: read the row from the local store and
      // skip (never throw) when it isn't there.
      const existing = await tx.run(
        zql.BlockPrintTask.where('orgId', ctx.orgId).where(
          'id',
          args.printTaskId,
        ),
      );
      for (const row of existing) {
        await tx.mutate.BlockPrintTask.delete({ orgId: ctx.orgId, id: row.id });
      }
    },
  ),
};

/** `workoutStatus` namespace mutators. */
export const workoutStatusMutators = {
  cancelBlockWorkout: defineWorkoutStatusMutator(
    PublishedWorkoutStatusDomain.CancelBlockWorkout.shape.input,
    async ({ tx, ctx, args }) => {
      if (tx.location === 'server') {
        const run = await getServerBranch(ctx, 'runCancelBlockWorkout');
        await run(tx, args as never);
        return;
      }

      // Optimistic cancel: mirror the server edit (getEditsForBlockWorkoutCancel)
      // — only still-planned layers flip to `canceled`. Rows live in the local
      // store only if their scope synced — read first and skip what isn't
      // there, never throw.
      const rows = await tx.run(
        zql.AthletePublishedWorkoutLayer.where('orgId', ctx.orgId)
          .where('blockId', args.blockId)
          .where('workoutId', args.workoutId),
      );
      for (const row of rows) {
        if (!row.isCurrent || row.status !== 'planned') {
          continue;
        }
        await tx.mutate.AthletePublishedWorkoutLayer.update({
          orgId: ctx.orgId,
          id: row.id,
          status: 'canceled',
          statusUpdatedAt: new Date().toISOString(),
        });
      }
    },
  ),

  updateAthleteWorkoutStatus: defineWorkoutStatusMutator(
    PublishedWorkoutStatusDomain.UpdateAthleteWorkoutStatus.shape.input,
    async ({ tx, ctx, args }) => {
      if (tx.location === 'server') {
        const run = await getServerBranch(ctx, 'runUpdateAthleteWorkoutStatus');
        await run(tx, args as never);
        return;
      }

      // Optimistic status flip per athlete, guarded the same way: read the
      // athlete's rows for the workout, update the current one, skip a
      // profile whose rows aren't in the local store.
      for (const update of args.updates) {
        const rows = await tx.run(
          zql.AthletePublishedWorkoutLayer.where('orgId', ctx.orgId)
            .where('blockId', args.blockId)
            .where('workoutId', args.workoutId)
            .where('profileId', update.profileId),
        );
        for (const row of rows) {
          if (!row.isCurrent) {
            continue;
          }
          await tx.mutate.AthletePublishedWorkoutLayer.update({
            orgId: ctx.orgId,
            id: row.id,
            status: update.status,
            // Restoring to `planned` clears the transition stamp, matching
            // the server edit (edits-for-workout-status.ts).
            statusUpdatedAt:
              update.status === 'planned' ? null : new Date().toISOString(),
          });
        }
      }
    },
  ),
};
